import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { analytics } from '../../services/analytics';
import SEOPageLayout from '../../components/SEOPageLayout';

const CheckIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const QuoteTierVsPandaDoc = () => {
  useEffect(() => {
    analytics.track('Comparison Page View', { comparison: 'QuoteTier vs PandaDoc' });
  }, []);

  return (
    <SEOPageLayout
      title="QuoteTier vs PandaDoc Alternative 2025 | Compare Features & Pricing"
      description="Looking for a PandaDoc alternative? Compare QuoteTier vs PandaDoc features, pricing, and ease of use. See why service businesses choose QuoteTier."
      canonical="/compare/quotetier-vs-pandadoc"
      ogTitle="QuoteTier vs PandaDoc - The Simpler Alternative"
      ogDescription="Compare QuoteTier and PandaDoc. QuoteTier offers interactive Good/Better/Best pricing that helps customers upsell themselves."
    >
      <section className="py-32 md:py-40 lg:py-48">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-tight mb-6">
              QuoteTier vs. <span className="text-primary font-bold">PandaDoc</span>
            </h1>
            <p className="font-jetbrains text-xl md:text-2xl text-gray-600 tracking-tight leading-relaxed mb-8">
              Need a PandaDoc alternative? QuoteTier offers simpler quoting with interactive pricing that increases deal size.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-6 px-6 font-instrument text-lg font-semibold text-gray-900 bg-gray-50">Feature</th>
                  <th className="text-center py-6 px-6 bg-black text-white">
                    <span className="font-instrument text-lg font-bold">QuoteTier</span>
                  </th>
                  <th className="text-center py-6 px-6 font-instrument text-lg font-semibold text-gray-700 bg-gray-50">PandaDoc</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100">
                  <td className="py-4 px-6 font-jetbrains font-medium">Starting Price</td>
                  <td className="py-4 px-6 text-center font-jetbrains font-bold text-green-600">$0/mo (Free tier)</td>
                  <td className="py-4 px-6 text-center font-jetbrains">$35/user/mo</td>
                </tr>
                <tr className="border-b border-gray-100 bg-gray-50/30">
                  <td className="py-4 px-6 font-jetbrains font-medium">Interactive Good/Better/Best Pricing</td>
                  <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                  <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-4 px-6 font-jetbrains font-medium">Automatic Upselling</td>
                  <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                  <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                </tr>
                <tr className="border-b border-gray-100 bg-gray-50/30">
                  <td className="py-4 px-6 font-jetbrains font-medium">E-Signatures</td>
                  <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                  <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-4 px-6 font-jetbrains font-medium">Document Generation</td>
                  <td className="py-4 px-6 text-center font-jetbrains">Quote-focused</td>
                  <td className="py-4 px-6 text-center font-jetbrains">All documents</td>
                </tr>
                <tr className="border-b border-gray-100 bg-gray-50/30">
                  <td className="py-4 px-6 font-jetbrains font-medium">Complexity</td>
                  <td className="py-4 px-6 text-center font-jetbrains text-green-600 font-bold">Simple</td>
                  <td className="py-4 px-6 text-center font-jetbrains">Enterprise-grade</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-4 px-6 font-jetbrains font-medium">Setup Time</td>
                  <td className="py-4 px-6 text-center font-jetbrains text-green-600 font-bold">5 minutes</td>
                  <td className="py-4 px-6 text-center font-jetbrains">Hours to days</td>
                </tr>
                <tr className="border-b border-gray-100 bg-gray-50/30">
                  <td className="py-4 px-6 font-jetbrains font-medium">CRM Integrations</td>
                  <td className="py-4 px-6 text-center font-jetbrains">Email-focused</td>
                  <td className="py-4 px-6 text-center font-jetbrains">Extensive</td>
                </tr>
                <tr>
                  <td className="py-4 px-6 font-jetbrains font-medium">Free Plan</td>
                  <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 text-green-600 mx-auto" /></td>
                  <td className="py-4 px-6 text-center font-jetbrains text-gray-500">Limited trial</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container max-w-4xl mx-auto px-4">
          <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-6">Why Choose QuoteTier Over PandaDoc?</h2>
          <div className="space-y-6 font-jetbrains text-lg text-gray-700">
            <p>
              <strong>PandaDoc</strong> is an all-in-one document automation platform built for enterprise teams. It handles contracts, proposals, quotes, and more. But that power comes with complexity and cost.
            </p>
            <p>
              <strong>QuoteTier</strong> does one thing exceptionally well: helps you create interactive quotes that increase your average deal size. No enterprise features you'll never use, no complex setup, no steep learning curve.
            </p>
            <h3 className="font-instrument text-2xl font-bold mt-8 mb-4">When to Choose QuoteTier:</h3>
            <ul className="list-disc pl-6 space-y-3">
              <li><strong>You're a Service Business:</strong> Contractors, freelancers, and agencies benefit most from our Good/Better/Best pricing approach.</li>
              <li><strong>You Want Simplicity:</strong> QuoteTier is designed to be learned in minutes, not days.</li>
              <li><strong>You Want Better Pricing:</strong> Start free with QuoteTier. PandaDoc's useful features start at $35/user/month.</li>
              <li><strong>You Want to Increase Deal Size:</strong> Our interactive pricing is proven to help customers choose higher-value options.</li>
            </ul>
            <h3 className="font-instrument text-2xl font-bold mt-8 mb-4">When PandaDoc Might Be Better:</h3>
            <ul className="list-disc pl-6 space-y-3">
              <li>You need a full document automation suite (contracts, agreements, etc.)</li>
              <li>You require extensive CRM integrations</li>
              <li>You have an enterprise sales team with complex workflows</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-black text-white">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-6">Try the Simpler Alternative</h2>
          <p className="font-jetbrains text-xl text-gray-300 mb-8">
            Create your first interactive quote in under 5 minutes. No credit card required.
          </p>
          <Link to="/signup">
            <Button className="px-10 py-4 font-instrument text-xl font-semibold bg-white text-black hover:bg-gray-200 rounded-lg shadow-lg">
              Start Free
            </Button>
          </Link>
        </div>
      </section>
    </SEOPageLayout>
  );
};

export default QuoteTierVsPandaDoc;
